import React, { useState, useRef, useEffect } from "react";
import {
  ScanBarcode,
  Clock,
  CheckCircle2,
  AlertCircle,
  AlertTriangle,
  Scan,
} from "lucide-react";
import { toast } from "@shared/hooks/use-toast";
import { useParams } from "react-router-dom";
import { useScanSlipLot, useSlipLotRecentScans } from "../../../../../hooks";
import { Button } from "@shared/ui/button";
import { Input } from "@shared/ui/input";

export const BarcodeTab = ({
  isLocked,
  disableAddEntry,
}: {
  isLocked?: boolean;
  disableAddEntry?: boolean;
}) => {
  const { id: lotId } = useParams<{ id: string }>();
  const scanMutation = useScanSlipLot(lotId!);
  const { data: recentScansData } = useSlipLotRecentScans(lotId!);
  const [inputValue, setInputValue] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (inputRef.current && !disableAddEntry) {
      inputRef.current.focus();
    }
  }, [disableAddEntry]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      processBarcode(inputValue.trim());
    }
  };

  const processBarcode = (payload: string) => {
    if (!payload || disableAddEntry) return;

    scanMutation.mutate(
      { payload },
      {
        onSuccess: (data: any) => {
          setInputValue("");
          const result = data.result || data;
          const slipNo =
            result.slipNumber || payload.split("|")[2] || "Unknown";
          toast({
            title: "Success",
            description: `Entry ${slipNo} scanned`,
          });
        },
        onError: (error: any) => {
          setInputValue("");
          toast({
            variant: "destructive",
            title: "Error",
            description:
              error.response?.data?.message ||
              "Barcode damaged — use manual entry for this slip",
          });
        },
      },
    );
  };

  const handleSimulateScan = (payload: string) => {
    if (!disableAddEntry) {
      processBarcode(payload);
    }
  };

  const result = recentScansData?.result || {};
  const scans = (result.scans || []).map((scan: any) => ({
    id: scan.id,
    rowNumber: scan.rowNumber,
    slipNumber: scan.slipNumber,
    consumerName: scan.consumerName,
    consumerAccount: scan.consumerAccount,
    amount: scan.amount,
    status: scan.status,
    statusDisplay: scan.statusDisplay,
    flagReason: scan.flagReason,
    iconType: scan.iconType,
    scannedAt: scan.scannedAt,
  }));

  const validCount = result.summary?.validCount || 0;
  const flaggedCount = result.summary?.flaggedCount || 0;

  return (
    <div className="mt-4 space-y-6">
      {/* Scanner Input Card */}
      <div className="bg-blue-50/50 border border-dashed border-blue-400 rounded-xl p-8 flex flex-col items-center justify-center relative overflow-hidden">
        <div className="bg-white p-2 rounded-xl shadow-sm mb-3">
          <ScanBarcode className="w-6 h-6 text-blue-600" />
        </div>

        <h2 className="text-base font-bold text-gray-900 mb-1">
          Point the scanner gun here
        </h2>

        <p className="text-xs text-gray-500 text-center max-w-xl mb-5 leading-relaxed">
          Ensure the text box below is focused. Each barcode encodes{" "}
          <span className="font-semibold text-gray-700">
            Centre | Consumer | Slip # | Amount | Date | Checksum.
          </span>
          <br />
          Validated within 1 second of scan. Fallback: click Manual Entry for a
          specific slip if the barcode is damaged.
        </p>

        <div className="w-full max-w-2xl relative">
          <Input
            ref={inputRef}
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Start scanning slips..."
            className="w-full max-w-md mx-auto h-12 text-lg text-center"
            autoFocus={!disableAddEntry}
            disabled={disableAddEntry}
          />
        </div>

        <div className="mt-6 flex gap-2 w-full max-w-md">
          <Button
            variant="outline"
            onClick={() =>
              handleSimulateScan(
                "DTW|ACCT-100021|SLP-001234|125.50|2026-04-22|CHK9",
              )
            }
            className="flex-1"
            disabled={disableAddEntry}
          >
            <Scan className="w-4 h-4 mr-2" />
            Scan Valid
          </Button>
          <Button
            variant="outline"
            onClick={() => handleSimulateScan("DTW|ACCT-100021|SLP-001234")}
            className="flex-1"
            disabled={disableAddEntry}
          >
            <Scan className="w-4 h-4 mr-2" />
            Scan Invalid
          </Button>
        </div>
      </div>

      {/* Recent Scans Section */}
      <div className="bg-white border rounded-xl overflow-hidden shadow-sm">
        <div className="flex items-center justify-between p-4 border-b bg-white">
          <div className="flex items-center gap-2">
            <Clock className="w-5 h-5 text-gray-500" />
            <h3 className="font-bold text-gray-900">Recent Scans</h3>
          </div>
          <div className="text-xs font-semibold text-gray-500">
            {validCount} valid · {flaggedCount} flagged
          </div>
        </div>

        <div className="divide-y divide-gray-100">
          {scans.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              No recent scans to display.
            </div>
          ) : (
            scans.map((scan: any) => {
              const isValid = scan.iconType === "valid";
              const isFlagged = scan.iconType === "flagged";
              const isUnreadable = scan.iconType === "unreadable";

              let bgClass = "bg-amber-50/50";
              let textClass = "text-amber-700 font-medium";
              if (isValid) {
                bgClass = "bg-green-50/50";
                textClass = "text-green-700 font-medium";
              } else if (isFlagged) {
                bgClass = "bg-red-50/50";
                textClass = "text-red-700 font-medium";
              }

              const detailText = isValid
                ? `Valid - ${scan.consumerAccount || "Unknown"} - $${scan.amount || "0.00"}`
                : `${scan.statusDisplay || "Flagged"}${scan.flagReason ? ` - ${scan.flagReason}` : ""}`;

              return (
                <div
                  key={scan.id}
                  className={`p-4 flex flex-col md:flex-row md:items-center justify-between gap-2 transition-colors ${bgClass}`}
                >
                  <div className="flex items-start gap-3">
                    <div className="mt-0.5">
                      {isValid && (
                        <CheckCircle2 className="w-5 h-5 text-green-600 fill-green-100" />
                      )}
                      {isFlagged && (
                        <AlertCircle className="w-5 h-5 text-red-600 fill-red-100" />
                      )}
                      {isUnreadable && (
                        <AlertTriangle className="w-5 h-5 text-amber-600 fill-amber-100" />
                      )}
                    </div>
                    <div>
                      <div className="font-bold text-gray-900 text-sm">
                        {scan.slipNumber || "Unknown Slip"}
                      </div>
                      <div className={`text-xs mt-0.5 ${textClass}`}>
                        {detailText}
                      </div>
                    </div>
                  </div>

                  <div className="text-xs text-gray-500 font-medium md:self-center ml-8 md:ml-0">
                    {scan.scannedAt}
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
};
